import DigitalButtonAudio from '@/assets/audio/digital-button.mp3';
import FingerSnapAudio from '@/assets/audio/finger-snap.mp3';
import GlockenspielDingAudio from '@/assets/audio/glockenspiel-ding.mp3';
import NotificationSweepAudio from '@/assets/audio/notification-sweep.mp3';
import SilentAudio from '@/assets/audio/silent-audio.mp3';
import {
  SoundTrack,
  isReadAloudTrack,
} from '@/components/toggle-sound/sound-settings.ts';
import { readAloud } from '@/utils/audio-utils/read-aloud.ts';
import { traceError } from '@/utils/trace-error.ts';

type AudioSoundTrack = Exclude<
  SoundTrack,
  SoundTrack.ReadAloudFemale | SoundTrack.ReadAloudMale
>;
type NotificationAudioType = 'silent' | AudioSoundTrack;

const audioFileMap: Record<AudioSoundTrack, string> = {
  [SoundTrack.DigitalButton]: DigitalButtonAudio,
  [SoundTrack.FingerSnap]: FingerSnapAudio,
  [SoundTrack.GlockenspielDing]: GlockenspielDingAudio,
  [SoundTrack.NotificationSweep]: NotificationSweepAudio,
};

const NotificationAudio: Partial<
  Record<NotificationAudioType, HTMLAudioElement>
> = {};

const getNotificationAudio = (
  audioType: NotificationAudioType = SoundTrack.DigitalButton,
) => {
  if (!NotificationAudio[audioType]) {
    if (audioType === 'silent') {
      NotificationAudio.silent = new Audio(SilentAudio);
    } else {
      NotificationAudio[audioType] = new Audio(audioFileMap[audioType]);
    }
  }

  return NotificationAudio[audioType]!;
};

export interface PlayNotificationAudioOptions {
  track?: SoundTrack;
  volume?: number;
  text?: string;
  ownerId?: string;
  priority?: boolean;
  interruptSpeech?: boolean;
}

export const playNotificationAudio = async (
  options?: PlayNotificationAudioOptions,
) => {
  const track = options?.track || SoundTrack.DigitalButton;
  const volume = options?.volume ?? 0.5;

  if (isReadAloudTrack(track)) {
    const spoken = readAloud(options?.text ?? '', {
      volume,
      ownerId: options?.ownerId ?? 'notification',
      gender: track === SoundTrack.ReadAloudMale ? 'male' : 'female',
      priority: options?.priority,
      interrupt: options?.interruptSpeech,
      onUnavailable: () => {
        void playNotificationAudio({
          ...options,
          track: SoundTrack.DigitalButton,
        }).catch(() => {});
      },
    });

    if (spoken) {
      return;
    }
  }

  const audioType = isReadAloudTrack(track) ? SoundTrack.DigitalButton : track;

  const audio = getNotificationAudio(audioType);

  audio.volume = Math.max(0, Math.min(1, volume)); // Clamp between 0 and 1
  audio.currentTime = 0;

  try {
    await audio.play();
  } catch (error) {
    traceError(
      new Error('Failed to play audio ' + (error as Error).message, {
        cause: error,
      }),
    );

    throw error;
  }
};

export const playNotificationAudioSilent = async () => {
  const audio = getNotificationAudio('silent');
  audio.currentTime = 0;
  await audio.play();
};
